import { SearchOutlined, FilterOutlined, CalendarOutlined } from '@ant-design/icons';
import { Button, Input, Space, InputNumber, DatePicker, Select, Checkbox } from 'antd';
import PropTypes from 'prop-types';
import { useRef, useState, useEffect } from 'react';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

/**
 * AdvancedColumnFilter - A comprehensive, production-ready filter component
 *
 * Supports 8 filter types with automatic URL synchronization:
 * - text: Text search input
 * - number: Number input with min/max constraints
 * - numberRange: Min/Max number range inputs
 * - date: Single date picker
 * - dateRange: Date range picker
 * - select: Single dropdown selection
 * - multiSelect: Multiple dropdown selection
 * - boolean: Checkbox filter
 *
 * @param {Object} props - Component props
 * @param {string} props.dataIndex - Column data key (required)
 * @param {function} props.onFilter - Filter handler (required)
 * @param {function} props.onReset - Reset handler (required)
 * @param {string} props.currentValue - Current filter value from URL
 * @param {string} props.type - Filter type (default: 'text')
 * @param {Array} props.options - Options for select/multiSelect [{label, value}]
 * @param {string} props.placeholder - Custom placeholder text
 * @param {ReactNode} props.icon - Custom filter icon
 * @param {string} props.dateFormat - Date format (default: 'YYYY-MM-DD')
 * @param {number} props.min - Minimum value for numbers
 * @param {number} props.max - Maximum value for numbers
 * @param {number} props.step - Step value for numbers (default: 1)
 */
const AdvancedColumnFilter = ({
  dataIndex,
  onFilter,
  onReset,
  currentValue = '',
  type = 'text',
  options = [],
  placeholder,
  icon,
  dateFormat = 'YYYY-MM-DD',
  min,
  max,
  step = 1,
}) => {
  const inputRef = useRef(null);
  const [value, setValue] = useState(currentValue);
  const [rangeMin, setRangeMin] = useState('');
  const [rangeMax, setRangeMax] = useState('');
  const [dateValue, setDateValue] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);

  // Initialize state from currentValue
  useEffect(() => {
    if (type === 'dateRange' && currentValue) {
      const [start, end] = currentValue.split(',');
      setDateRange([start ? dayjs(start) : null, end ? dayjs(end) : null]);
    } else if (type === 'date' && currentValue) {
      setDateValue(currentValue ? dayjs(currentValue) : null);
    } else if (type === 'numberRange' && currentValue) {
      const [min, max] = currentValue.split(',');
      setRangeMin(min || '');
      setRangeMax(max || '');
    } else {
      setValue(currentValue);
    }
  }, [currentValue, type]);

  // Get appropriate icon based on type
  const getFilterIcon = () => {
    if (icon) return icon;

    const iconStyle = { color: currentValue ? '#1890ff' : undefined };

    switch (type) {
      case 'date':
      case 'dateRange':
        return <CalendarOutlined style={iconStyle} />;
      case 'select':
      case 'multiSelect':
        return <FilterOutlined style={iconStyle} />;
      default:
        return <SearchOutlined style={iconStyle} />;
    }
  };

  // Get placeholder text
  const getPlaceholder = () => {
    if (placeholder) return placeholder;

    switch (type) {
      case 'number':
        return `Enter ${dataIndex}`;
      case 'date':
        return 'Select date';
      case 'dateRange':
        return 'Select date range';
      case 'select':
      case 'multiSelect':
        return `Select ${dataIndex}`;
      case 'numberRange':
        return 'Enter range';
      default:
        return `Search ${dataIndex}`;
    }
  };

  // Handle filter apply
  const handleApply = () => {
    let filterValue;

    switch (type) {
      case 'numberRange':
        filterValue = rangeMin || rangeMax ? `${rangeMin || ''},${rangeMax || ''}` : '';
        break;
      case 'date':
        filterValue = dateValue ? dateValue.format(dateFormat) : '';
        break;
      case 'dateRange':
        if (dateRange && dateRange[0] && dateRange[1]) {
          filterValue = `${dateRange[0].format(dateFormat)},${dateRange[1].format(dateFormat)}`;
        } else {
          filterValue = '';
        }
        break;
      default:
        filterValue = value;
    }

    onFilter(dataIndex, filterValue);
  };

  // Handle reset
  const handleClear = clearFilters => {
    setValue('');
    setRangeMin('');
    setRangeMax('');
    setDateValue(null);
    setDateRange([null, null]);

    if (clearFilters) clearFilters();
    onReset(dataIndex);
  };

  // Render filter input based on type
  const renderFilterInput = setSelectedKeys => {
    switch (type) {
      case 'number':
        return (
          <InputNumber
            ref={inputRef}
            placeholder={getPlaceholder()}
            value={value}
            min={min}
            max={max}
            step={step}
            onChange={val => {
              setValue(val);
              setSelectedKeys(val !== null && val !== undefined ? [val.toString()] : []);
            }}
            onPressEnter={handleApply}
            style={{ width: '100%', marginBottom: 8 }}
          />
        );

      case 'numberRange':
        return (
          <Space direction="vertical" style={{ width: '100%' }}>
            <InputNumber
              placeholder="Min"
              value={rangeMin}
              min={min}
              max={max}
              step={step}
              onChange={val => {
                setRangeMin(val);
                const rangeStr = `${val || ''},${rangeMax || ''}`;
                setSelectedKeys(val || rangeMax ? [rangeStr] : []);
              }}
              style={{ width: '100%' }}
            />
            <InputNumber
              placeholder="Max"
              value={rangeMax}
              min={min}
              max={max}
              step={step}
              onChange={val => {
                setRangeMax(val);
                const rangeStr = `${rangeMin || ''},${val || ''}`;
                setSelectedKeys(rangeMin || val ? [rangeStr] : []);
              }}
              style={{ width: '100%', marginBottom: 8 }}
            />
          </Space>
        );

      case 'date':
        return (
          <DatePicker
            ref={inputRef}
            value={dateValue}
            format={dateFormat}
            onChange={date => {
              setDateValue(date);
              setSelectedKeys(date ? [date.format(dateFormat)] : []);
            }}
            style={{ width: '100%', marginBottom: 8 }}
          />
        );

      case 'dateRange':
        return (
          <RangePicker
            ref={inputRef}
            value={dateRange}
            format={dateFormat}
            onChange={dates => {
              setDateRange(dates);
              if (dates && dates[0] && dates[1]) {
                const rangeStr = `${dates[0].format(dateFormat)},${dates[1].format(dateFormat)}`;
                setSelectedKeys([rangeStr]);
              } else {
                setSelectedKeys([]);
              }
            }}
            style={{ width: '100%', marginBottom: 8 }}
          />
        );

      case 'select':
        return (
          <Select
            ref={inputRef}
            placeholder={getPlaceholder()}
            value={value || undefined}
            onChange={val => {
              setValue(val);
              setSelectedKeys(val ? [val] : []);
            }}
            style={{ width: '100%', marginBottom: 8 }}
            showSearch
            allowClear
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={options}
          />
        );

      case 'multiSelect':
        return (
          <Select
            ref={inputRef}
            mode="multiple"
            placeholder={getPlaceholder()}
            value={value ? value.split(',').filter(Boolean) : []}
            onChange={values => {
              const valueStr = values.join(',');
              setValue(valueStr);
              setSelectedKeys(valueStr ? [valueStr] : []);
            }}
            style={{ width: '100%', marginBottom: 8 }}
            showSearch
            allowClear
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={options}
          />
        );

      case 'boolean':
        return (
          <Checkbox
            checked={value === 'true'}
            onChange={e => {
              const val = e.target.checked ? 'true' : '';
              setValue(val);
              setSelectedKeys(val ? [val] : []);
            }}
            style={{ marginBottom: 8 }}
          >
            {placeholder || `Filter ${dataIndex}`}
          </Checkbox>
        );

      default: // text
        return (
          <Input
            ref={inputRef}
            placeholder={getPlaceholder()}
            value={value}
            onChange={e => {
              const val = e.target.value;
              setValue(val);
              setSelectedKeys(val ? [val] : []);
            }}
            onPressEnter={handleApply}
            style={{ marginBottom: 8, display: 'block' }}
          />
        );
    }
  };

  return {
    filterDropdown: ({ setSelectedKeys, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()}>
        {renderFilterInput(setSelectedKeys)}
        <Space>
          <Button
            type="primary"
            onClick={() => {
              handleApply();
              close();
            }}
            icon={type === 'text' || type === 'number' ? <SearchOutlined /> : <FilterOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            {type === 'select' || type === 'multiSelect' ? 'Filter' : 'Search'}
          </Button>
          <Button onClick={() => handleClear(clearFilters)} size="small" style={{ width: 90 }}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: getFilterIcon,
    filteredValue: currentValue ? [currentValue] : null,
  };
};

AdvancedColumnFilter.propTypes = {
  dataIndex: PropTypes.string.isRequired,
  onFilter: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  currentValue: PropTypes.string,
  type: PropTypes.oneOf([
    'text',
    'number',
    'numberRange',
    'date',
    'dateRange',
    'select',
    'multiSelect',
    'boolean',
  ]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  placeholder: PropTypes.string,
  icon: PropTypes.node,
  dateFormat: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
};

export default AdvancedColumnFilter;
